/** Cash-register amount entry: an integer-cents accumulator.
 *
 *  Every digit shifts left — `1` `2` `9` `9` reads out as `$12.99`, with
 *  no decimal point to type. This is how physical registers and Square's
 *  Custom Amount both behave, and it's the reason we don't parse free
 *  text here: there's no way to enter an ambiguous or malformed amount,
 *  so downstream code never sees a NaN or a half-typed "12.".
 */

/** $999,999.99. Above this the accumulator ignores further digits
 *  rather than silently wrapping or overflowing the display. */
export const MAX_CENTS = 99_999_999;

export type AmountKey = string;

/** Apply one keypad press to the current cents value.
 *
 *  Accepts `'0'`–`'9'`, `'00'`, `'back'`, and `'clear'`. Anything else
 *  is returned unchanged, so callers can pass raw key names without
 *  filtering first.
 */
export function applyAmountKey(cents: number, key: AmountKey): number {
  if (key === 'clear') return 0;
  if (key === 'back') return Math.floor(cents / 10);
  if (key === '00') {
    const next = cents * 100;
    return next > MAX_CENTS ? cents : next;
  }
  if (!/^[0-9]$/.test(key)) return cents;
  const next = cents * 10 + Number(key);
  return next > MAX_CENTS ? cents : next;
}

/** Map a `KeyboardEvent` to a keypad key, or null if it isn't one.
 *
 *  `numpadOnly` restricts matching to the physical number pad. That's
 *  what lets the custom-amount panel route numpad presses to the price
 *  even while the cashier is typing a name in a text field: `code`
 *  distinguishes `Numpad1` from `Digit1`, which `key` cannot.
 */
export function amountKeyFromEvent(
  e: Pick<KeyboardEvent, 'key' | 'code'>,
  numpadOnly = false,
): AmountKey | null {
  const numpadDigit = /^Numpad([0-9])$/.exec(e.code);
  if (numpadDigit) return numpadDigit[1];
  if (e.code === 'NumpadBackspace') return 'back';
  if (numpadOnly) return null;
  if (/^[0-9]$/.test(e.key)) return e.key;
  if (e.key === 'Backspace') return 'back';
  if (e.key === 'Delete' || e.key === 'Escape') return 'clear';
  return null;
}
